import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col,
  Row,
  Spin,
  Table,
  Radio,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

// Import Components:
import FormDetailView from '../CompletedForms/FormDetailView';
import FormsTable from '../FormsTable';

// Import Actions:
import { getFormApprovals, getFormById, clearFormSelection, updateReadStatus } from '../state/forms.actions';

// Import Helpers:
import {
  getIdMap,
  includesTerm,
  removeDuplicateArrayValues,
  toTitleCase,
} from '../../helpers/helpers';
import {
  getFilters,
  parseFormDates,
  setIncludesTerm,
  getFormDisplayFieldColumns,
  getSectionPermissionMap,
} from '../formHelpers';
import { getApproveFormColumns } from '../FormColumns';
import SheetUploadPrompt from '../../common/excel/SheetUploadPrompt';
import ApproveFormImportDrawer from './ApproveFormImportDrawer';
import FormSearchInput from '../FormSearchInput';

import useFormSearch from '../../common/hooks/useFormSearch';
import useToggle from '../../common/hooks/useToggle';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import OnTraccrRadioGroup from '../../common/inputs/OnTraccrRadioGroup';

import { parseFormResponseToReadableFormat } from '../ResponderHelpers';
import Permissions from '../../auth/Permissions';
import { formatProjectLabelFromCompanySettings } from '../../projects/projectHelpers';

const AGE_FILTER_ALL = 'all';
const AGE_FILTER_TODAY = 'today';
const AGE_FILTER_WEEK = 'week';
const AGE_FILTER_OLD = 'old';

/** Approve Forms Section */
function ApproveForms({
  onCardColumnClick,
  hideHeader = false,
  registerFormHeaderAction,
  dataLimit = null,
  setApprovalData = () => {},
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const formApprovals = useSelector((state) => state.forms.approvals);
  const customers = useSelector((state) => state.customers.customers);
  const projects = useSelector((state) => state.projects.projects);
  const formStatuses = useSelector((state) => state.forms.statuses);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const templates = useSelector((state) => state.forms.templates);
  const users = useSelector((state) => state.users.users);
  const positionNames = useSelector((state) => state.settings.positionNames);

  const [showApprovalFormDetail, setShowApprovalFormDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchStr, setSearchStr] = useState();
  const [uploadFile, setUploadFile] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});
  const [ageFilter, setAgeFilter] = useState(AGE_FILTER_ALL);

  const { isToggled: isExpanded, toggle: toggleExpanded } = useToggle(true);

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const userMap = useMemo(() => getIdMap(users), [users]);

  const searchParams = useMemo(() => ({
    searchTerm: searchStr,
    formIds: Object.keys(formApprovals),
  }), [searchStr, formApprovals]);
  const {
    searchSet,
    searchLoading,
  } = useFormSearch(searchParams);

  useEffect(() => {
    dispatch(getFormApprovals({ getFormData: true }));
  }, []);

  // Handlers:
  // -----------------------------------------------------
  const onApproveFormClick = useCallback(async (record) => {
    if (loading) return;
    setLoading(true);
    const result = await dispatch(getFormById(record.formId, { getRecentDraft: true }));
    if (result) {
      setShowApprovalFormDetail(true);
    }
    setLoading(false);
  }, [loading]);

  const onSearch = useCallback((e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setSearchStr(value);
  }, []);

  const onFormDetailClose = useCallback(() => {
    setShowApprovalFormDetail(false);
    dispatch(clearFormSelection());
  }, [dispatch]);

  const uploadHeaderAction = useMemo(() => (
    <SheetUploadPrompt
      onFileChange={setUploadFile}
      hideUploadMessage
      showUploadList={false}
      buttonClassName="forms-subpage-action-button"
      buttonType="secondary"
      iconLeft
    />
  ), []);

  useEffect(() => {
    if (!registerFormHeaderAction) return undefined;
    registerFormHeaderAction(uploadHeaderAction);
    return () => registerFormHeaderAction(null);
  }, [registerFormHeaderAction, uploadHeaderAction]);

  // Prepare data:
  // -----------------------------------------------------
  const baseApprovalData = useMemo(() => Object.values(formApprovals).map(
    (approvalForm) => {
      const {
        users = [],
        userId,
        projects: formProjects = [],
        customers: formCustomers = [],
      } = approvalForm;
      const fullUsers = removeDuplicateArrayValues(users.concat([userId]));
      const userNameList = new Set();
      fullUsers.forEach((assignedUserId) => {
        const {
          [assignedUserId]: { name: userName } = {},
        } = userMap;
        if (userName) userNameList.add(userName);
      });

      const customerNames = [];
      formCustomers.forEach((customerId) => {
        const {
          [customerId]: {
            name: customerName,
          } = {},
        } = customers;

        customerNames.push(customerName);
      });

      const projectNames = [];
      formProjects.forEach((projectId) => {
        const {
          [projectId]: {
            name: projectName,
          } = {},
        } = projectIdMap;

        projectNames.push(projectName);
      });

      const {
        lastUpdatedDate,
        createdAtDate,
      } = parseFormDates(approvalForm);

      return {
        ...approvalForm,
        users: fullUsers,
        customer: customerNames.join(', '),
        project: projectNames.join(', '),
        userNameList,
        lastUpdatedDate,
        createdAtDate,
      };
    },
  )
    .filter(({ editable, editableApprovals }) => (!editable || editableApprovals)), [
    formApprovals,
    userMap,
    projectIdMap,
    customers,
  ]);

  const ageFilteredDataMap = useMemo(() => {
    const dateAgeMap = {
      [AGE_FILTER_ALL]: baseApprovalData,
      [AGE_FILTER_TODAY]: [],
      [AGE_FILTER_WEEK]: [],
      [AGE_FILTER_OLD]: [],
    };
    const now = DateTime.local();
    baseApprovalData.reduce((acc, form) => {
      const days = now.diff(DateTime.fromMillis(form.lastUpdated), 'days').days;
      if (days < 1) dateAgeMap[AGE_FILTER_TODAY].push(form);
      if (days < 7) dateAgeMap[AGE_FILTER_WEEK].push(form);
      if (days >= 7) dateAgeMap[AGE_FILTER_OLD].push(form);
      return acc;
    }, dateAgeMap);
    return dateAgeMap;
  }, [baseApprovalData]);

  const approvalData = useMemo(() => ageFilteredDataMap[ageFilter]
    .filter(({
      formId,
      templateName,
      number,
      project,
      customer,
      status,
      userNameList,
    }) => (
      (!searchStr || searchSet?.has(formId))
      || (templateName && includesTerm(templateName, searchStr))
      || (userNameList && setIncludesTerm(userNameList, searchStr))
      || (number && includesTerm(number.toString(), searchStr))
      || (project && includesTerm(project, searchStr))
      || (customer && includesTerm(customer, searchStr))
      || (status && includesTerm(status, searchStr))
    ))
    .sort((a, b) => b.lastUpdated - a.lastUpdated)
    .slice(0, dataLimit || undefined), [
    ageFilteredDataMap,
    ageFilter,
    searchStr,
    searchSet,
  ]);

  const ageFilterOptions = useMemo(() => [
    { label: 'All', value: AGE_FILTER_ALL, count: ageFilteredDataMap[AGE_FILTER_ALL].length },
    { label: 'Today', value: AGE_FILTER_TODAY, count: ageFilteredDataMap[AGE_FILTER_TODAY].length },
    { label: 'This week', value: AGE_FILTER_WEEK, count: ageFilteredDataMap[AGE_FILTER_WEEK].length },
    { label: '>7 days', value: AGE_FILTER_OLD, count: ageFilteredDataMap[AGE_FILTER_OLD].length },
  ], [ageFilteredDataMap]);

  useEffect(() => {
    setApprovalData?.(baseApprovalData);
  }, [baseApprovalData, setApprovalData]);

  // Prepare filters:
  // -----------------------------------------------------
  const approveUsersFilters = useMemo(() => getFilters({ data: approvalData, idMap: userMap, key: 'userId' }), [approvalData, userMap]);
  const approveProjectFilters = useMemo(() => getFilters({
    data: approvalData,
    idMap: projectIdMap,
    key: 'projects',
    formatter: ({ name, number }) => (
      { name: formatProjectLabelFromCompanySettings({ name, number, settings }) }
    ),
  }), [approvalData, projectIdMap, settings]);
  const approveCustomerFilters = useMemo(() => getFilters({ data: approvalData, idMap: customers, key: 'customers' }), [approvalData, customers]);
  const approveStatusFilters = useMemo(() => (
    Object.values(formStatuses)
      .map((formStatus) => ({ text: toTitleCase(formStatus.status), value: formStatus.id }))
  ), [formStatuses]);
  const cardFilters = useMemo(() => {
    const cardSet = new Set(
      approvalData.map(({ card }) => card)
        .filter(Boolean),
    );
    return Array.from(cardSet).map((card) => ({ text: card, value: card }));
  }, [approvalData]);

  const relevantTemplates = useMemo(() => {
    const relevantTemplateMap = {};
    const relevantTemplateIds = new Set();
    approvalData.forEach(({
      templateId,
    }) => {
      if (!relevantTemplateIds.has(templateId)) {
        relevantTemplateIds.add(templateId);

        const relevantTemplate = templates[templateId];
        if (!relevantTemplate) return;

        let relevantSections = [];
        try {
          const parsedTemplateData = JSON.parse(relevantTemplate?.jsonFormData);
          relevantSections = parsedTemplateData?.sections ?? [];
        } catch (err) {
          // do nothing
        }

        const fieldMap = relevantSections?.reduce((acc, { fields = [] }) => {
          fields.forEach((field) => {
            acc[field.id] = field;
          });
          return acc;
        }, {});

        relevantTemplateMap[templateId] = {
          ...relevantTemplate,
          sections: relevantSections,
          fieldMap,
          columns: getFormDisplayFieldColumns({ relevantTemplate, fieldMap}),
        };
      }
    });

    return relevantTemplateMap;
  }, [approvalData, templates]);

  const templateSectionPermissionMap = useMemo(() => (
    Object.values(relevantTemplates).reduce((acc, template) => {
      acc[template.id] = getSectionPermissionMap({
        sections: template.sections,
        users,
        positionNames,
        userId: Permissions.id,
      });
      return acc;
    }, {})
  ), [relevantTemplates, users, positionNames]);

  const handleToggleReadStatus = useCallback(async (formId, isRead, onlyInFormDrafts) => {
    setLoadingStates((prev) => ({ ...prev, [formId]: true }));
    await dispatch(updateReadStatus(formId, isRead, 'approval', onlyInFormDrafts));
    setLoadingStates((prev) => ({ ...prev, [formId]: false }));
  }, []);

  // Get columns:
  // -----------------------------------------------------
  const approvalColumns = useMemo(() => (
    getApproveFormColumns({
      usersFilters: approveUsersFilters,
      projectFilters: approveProjectFilters,
      customerFilters: approveCustomerFilters,
      statusFilters: approveStatusFilters,
      cardFilters,
      userMap,
      projectIdMap,
      settings,
      t,
      templates: relevantTemplates,
      templateSectionPermissionMap,
      onCardColumnClick,
      handleToggleReadStatus,
      loadingStates,
    })
  ), [
    approveUsersFilters,
    approveProjectFilters,
    approveCustomerFilters,
    approveStatusFilters,
    cardFilters,
    userMap,
    relevantTemplates,
    templateSectionPermissionMap,
    onCardColumnClick,
    handleToggleReadStatus,
    loadingStates,
  ]);

  const rowExpandable = useCallback((record) => {
    const relevantTemplate = relevantTemplates[record.templateId];
    return relevantTemplate?.displayFields?.length;
  }, [relevantTemplates]);

  const expandedRowRender = useCallback((record) => {
    const relevantTemplate = relevantTemplates[record.templateId];
    const formResponses = parseFormResponseToReadableFormat({
      templateSections: relevantTemplate?.sections ?? [],
      responseSections: record?.data?.sections ?? [],
      projectIdMap,
      settings,
      enableTable: true,
    });

    return (
      <Table
        columns={relevantTemplate?.columns ?? []}
        dataSource={[formResponses]}
        pagination={false}
        size="small"
        bordered
      />
    );
  }, [relevantTemplates]);

  useEffect(() => {
    if (isExpanded) {
      setExpandedRowKeys(approvalData.map(({ id }) => id));
    } else {
      setExpandedRowKeys([]);
    }
  }, [approvalData, isExpanded]);

  const onExpand = useCallback((event, { id }) => {
    if (!event) {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== id));
      return;
    }

    setExpandedRowKeys([...expandedRowKeys, id]);
  }, [expandedRowKeys]);

  const expandable = useMemo(() => ({
    defaultExpandAllRows: true,
    rowExpandable,
    expandedRowRender,
    expandedRowKeys,
    onExpand,
    columnWidth: 50,
  }), [approvalData, rowExpandable, expandedRowRender, expandedRowKeys, onExpand]);

  return (
    <div>
      { !hideHeader && (
        <Row className="forms-list-header" align="middle" justify="space-between" gutter={20}>
          <Col>
            <FormSearchInput
              searchStr={searchStr}
              searchLoading={searchLoading}
              onSearch={onSearch}
              testId="formsApproveSearch"
            />
          </Col>
          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <OnTraccrRadioGroup
              value={ageFilter}
              size="medium"
              buttonStyle="outline"
              className="forms-home-tab-toggle"
              onChange={(newAgeFilter) => setAgeFilter(newAgeFilter)}
            >
              {ageFilterOptions.map(({ label, value, count }) => (
                <Radio.Button key={value} value={value}>
                  {label}
                  {' '}
                  <span className="forms-home-tab-toggle-count">{count}</span>
                </Radio.Button>
              ))}
            </OnTraccrRadioGroup>
            <OnTraccrButton
              title={isExpanded ? 'Collapse All' : 'Expand All'}
              type="secondary"
              onClick={toggleExpanded}
              icon={isExpanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
              iconLeft
              style={{ marginLeft: 10, marginRight: 5 }}
              className="forms-subpage-action-button"
            />
          </Col>
        </Row>
      )}
      <div>
        <FormsTable
          data={approvalData}
          columns={approvalColumns}
          onClickRow={onApproveFormClick}
          expandable={expandable}
        />
        <FormDetailView
          visible={showApprovalFormDetail}
          onClose={onFormDetailClose}
          userMap={userMap}
          startOnFields
          onCardColumnClick={onCardColumnClick}
          zIndex={999}
        />
      </div>
      {loading && (
        <Row justify="center" align="middle" className="form-loading-container">
          <Spin size="large" />
        </Row>
      )}
      <ApproveFormImportDrawer
        onClose={() => setUploadFile(null)}
        uploadFile={uploadFile}
      />
    </div>
  );
}

ApproveForms.propTypes = {
  onCardColumnClick: PropTypes.func.isRequired,
  hideHeader: PropTypes.bool,
  registerFormHeaderAction: PropTypes.func,
  dataLimit: PropTypes.number,
  setApprovalData: PropTypes.func,
};

ApproveForms.defaultProps = {
  hideHeader: false,
  registerFormHeaderAction: null,
  dataLimit: null,
  setApprovalData: () => {},
};

export default ApproveForms;
